export const galaContent = {
  event: {
    edition: "Edition II",
    title: "The Paulina Porizkova Gala",
    subtitle:
      "A sequel match-giving evening for emergency medical services supporting girls, families, and frontline caregivers affected by the US/Iran conflict zone crisis.",
    date: "Friday, June 19, 2026",
    venue: "The Corinthian Hall, New York · donor details refined privately after qualification",
    minimumGift: 100000,
    destination:
      "Directed toward emergency medical services, mobile clinics, trauma care, and dignified support for women and girls through trusted humanitarian partners.",
  },
  palette: [
    { name: "Crisis Crimson", hex: "#990000", note: "Urgency, responsibility, and focus." },
    { name: "Gilded Relief", hex: "#C5A059", note: "Prestige, generosity, and ceremonial warmth." },
    { name: "Midnight Black", hex: "#050505", note: "Gravity, privacy, and museum-like restraint." },
    { name: "Smoked Onyx", hex: "#121212", note: "Layered editorial depth for cards and overlays." },
  ],
  typography: [
    { name: "Bodoni Moda", role: "Editorial headlines and section titles" },
    { name: "Outfit", role: "Body copy, donor instructions, and navigation" },
    { name: "Playfair Display", role: "Occasional pull quotes and ceremonial accents" },
  ],
  motionNotes: [
    "Hero background drifts vertically with scroll to create a restrained parallax reveal.",
    "Donation portal uses a mouse-tracking spotlight that follows the donor’s cursor across the glass surface.",
    "Editorial cards fade upward in staggered sequence to guide attention without sensationalising the subject matter.",
    "Progress bars and commitment figures animate softly on entry rather than using aggressive countdowns.",
  ],
  wireframe: [
    "Hero / qualification + minimum gift",
    "Crisis desk / source-backed briefings + timeline",
    "Mission / women, girls, emergency medicine",
    "Hosts & guests / editorial bento gallery",
    "Partners + inaugural impact / credibility and sequel proof",
    "Donation portal / secure checkout + concierge pledge",
  ],
  heroStats: [
    { label: "Humanitarian partners", value: "MSF · ICRC" },
    { label: "Giving format", value: "Match-giving Edition II" },
  ],
  newsBriefs: [
    {
      outlet: "BBC",
      date: "April 10, 2026",
      title: "Even if fighting stops, the damage to Iran’s children will endure",
      summary:
        "BBC reporting focused on trauma among children, disrupted schooling, anxiety, and testimony from families and counsellors, noting the longer-term emotional burden that war places on young people and caregivers.",
      sourceUrl: "https://www.bbc.com/news/articles/ce84ngmjz8zo",
    },
  ],
  timeline: [
    {
      year: "1979",
      title: "Diplomatic rupture",
      body:
        "The Iranian Revolution and hostage crisis permanently reshaped US-Iran relations and set a long arc of mistrust.",
    },
    {
      year: "1988",
      title: "Tanker war lessons",
      body:
        "Conflict in Gulf shipping lanes demonstrated how regional confrontation can quickly become a civilian and humanitarian logistics emergency.",
    },
    {
      year: "2015",
      title: "Nuclear diplomacy window",
      body:
        "The Joint Comprehensive Plan of Action briefly lowered tensions and showed that negotiated de-escalation was possible.",
    },
    {
      year: "2018",
      title: "Sanctions pressure returns",
      body:
        "US withdrawal from the nuclear deal reintroduced severe economic pressure and sharpened regional instability.",
    },
    {
      year: "2020",
      title: "Direct escalation shock",
      body:
        "The killing of Qassem Soleimani and retaliatory strikes renewed fears of a larger regional war.",
    },
    {
      year: "2026",
      title: "Ceasefire under strain",
      body:
        "Current reporting points to a fragile pause in direct hostilities, continued disruption in Hormuz, and severe humanitarian consequences for civilians and medical access.",
    },
  ],
  missionPillars: [
    {
      title: "Emergency medical response",
      text: "Support mobile trauma units, surgical stabilisation, emergency transport, and rapid deployment equipment where health systems are strained.",
    },
    {
      title: "Girls and families first",
      text: "Back dignified care pathways that keep women and girls connected to maternal health, protection services, and safe continuity of treatment.",
    },
    {
      title: "Respectful humanitarian delivery",
      text: "Concentrate funding through credible relief partners whose medical neutrality and operational discipline match the seriousness of the moment.",
    },
  ],
  testimonials: [
    {
      title: "Case study · anonymised clinical transport",
      quote:
        "When roads were cut and electricity failed intermittently, a coordinated emergency transport chain meant patients still reached operative care in time.",
    },
    {
      title: "Case study · family recovery support",
      quote:
        "The most urgent need was not only treatment, but a safe handoff between trauma care, women’s services, and reliable follow-up for children.",
    },
    {
      title: "Case study · mobile clinic resilience",
      quote:
        "Flexible field teams mattered because the needs kept moving faster than fixed infrastructure could adapt.",
    },
  ],
  guestProfiles: [
    {
      name: "Paulina Porizkova",
      role: "Host · Founder of Edition II",
      bio:
        "Paulina anchors the sequel gala around dignity, urgency, and humanity-first storytelling—framing major philanthropy as disciplined care for families living through instability.",
      imageBadge: "Provided guest portrait",
      image:
        "https://customer-assets.emergentagent.com/job_relief-gala-sequel/artifacts/omjlcl5c_IMG_1424.jpeg",
    },
    {
      name: "Ryan Seacrest",
      role: "Red Carpet Host",
      bio:
        "Ryan’s role is to translate public attention into purposeful giving, helping keep the evening’s spotlight on medical access, credibility, and measurable relief.",
      imageBadge: "Provided guest portrait",
      imagePosition: "center top",
      image:
        "https://customer-assets.emergentagent.com/job_f7f7066b-d7d0-4f4b-a1ae-d17aff60b588/artifacts/s0u729ed_tTA4ZCSv05sZEEOB_9ZFM_86JsW4sl_00001.jpg",
    },
    {
      name: "Sofia Vergara",
      role: "Red Carpet Host",
      bio:
        "Sofia brings global visibility and warmth to the gala, reinforcing that glamour here serves a serious humanitarian objective rather than spectacle alone.",
      imageBadge: "Provided guest portrait",
      image:
        "https://customer-assets.emergentagent.com/job_relief-gala-sequel/artifacts/yeh7atei_IMG_1441.jpeg",
    },
    {
      name: "Viola Davis",
      role: "Special Guest",
      bio:
        "Viola’s presence resonates with the gala’s insistence on moral clarity, voice, and the protection of families whose stories too often disappear behind geopolitics.",
      imageBadge: "Provided guest portrait",
      imagePosition: "center top",
      image:
        "https://customer-assets.emergentagent.com/job_relief-gala-sequel/artifacts/m6va5rua_IMG_1438.jpeg",
    },
    {
      name: "Petra Van Bremen",
      role: "Special Guest",
      bio:
        "Petra’s alignment with education, dignity, and women-and-girls advocacy mirrors the gala’s core mission: relief that protects both immediate survival and longer-term human possibility.",
      imageBadge: "Provided guest portrait",
      image:
        "https://customer-assets.emergentagent.com/job_f7f7066b-d7d0-4f4b-a1ae-d17aff60b588/artifacts/g9scqxn7_tTLBFRtkYPIRQ0gpdRxKD_xvnJIPgD_00001.jpg",
    },
    {
      name: "Dagmar Havlová",
      role: "Special Guest",
      bio:
        "Dagmar’s presence evokes European civic conscience and the belief that culture and humanitarian responsibility can move in deliberate partnership.",
      imageBadge: "Provided guest portrait",
      imagePosition: "center top",
      image:
        "https://customer-assets.emergentagent.com/job_f7f7066b-d7d0-4f4b-a1ae-d17aff60b588/artifacts/oay260wx_45eaqy1o5ICF6lVgp-3uI_Ri4eHTxb_00001.jpg",
    },
  ],
  partners: [
    {
      name: "Médecins Sans Frontières (MSF)",
      description:
        "Recognised globally for independent medical humanitarian action, including emergency and conflict-zone support where access, staffing, and continuity of care are under pressure.",
    },
    {
      name: "International Committee of the Red Cross (ICRC)",
      description:
        "A long-standing humanitarian institution focused on civilian protection, medical neutrality, essential relief access, and the laws of armed conflict.",
    },
  ],
  inauguralImpact: [
    { label: "Medical convoys financed", value: "18" },
    { label: "Emergency care touchpoints", value: "42,000+" },
    { label: "Women-and-girls support packages", value: "11,600" },
    { label: "Partner-led response zones", value: "7" },
  ],
  imagery: {
    hero:
      "https://images.unsplash.com/photo-1636566891061-27e79f3e0be6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwcmVkJTIwZ29sZCUyMGFic3RyYWN0JTIwdGV4dHVyZXxlbnwwfHx8fDE3NzU4NTc0OTB8MA&ixlib=rb-4.1.0&q=85",
    gala:
      "https://images.unsplash.com/photo-1768508950778-9ba70d4445e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxlbGVnYW50JTIwYmxhY2slMjB0aWUlMjBnYWxhJTIwZXZlbnR8ZW58MHx8fHwxNzc1ODU3NDc5fDA&ixlib=rb-4.1.0&q=85",
    mission:
      "https://customer-assets.emergentagent.com/job_relief-gala-sequel/artifacts/o4mzc8ve_Sad%20Iranian%20Mother%20and%20Daughter.png",
  },
};

export const navigation = [
  { id: "crisis-desk", label: "Crisis Desk" },
  { id: "mission", label: "Mission" },
  { id: "guests", label: "Guests" },
  { id: "history", label: "Impact" },
  { id: "donation-portal", label: "Donate" },
];